import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import { useDrag, useDrop } from 'react-dnd'

const DRAG_TYPES = {
  GROUP: 'FIELD_MAPPING_GROUP',
  FIELD: 'FIELD_MAPPING_FIELD'
}

function DraggableGroupRow ({ groupIndex, children, onMoveGroup, className, ...rest }) {
  const ref = useRef(null)

  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPES.GROUP,
    item: () => ({ groupIndex }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  })

  const [, drop] = useDrop({
    accept: DRAG_TYPES.GROUP,
    hover (item, monitor) {
      if (!ref.current) return
      const dragIdx = item.groupIndex
      const hoverIdx = groupIndex
      if (dragIdx === hoverIdx) return

      const rect = ref.current.getBoundingClientRect()
      const midY = (rect.bottom - rect.top) / 2
      const clientY = monitor.getClientOffset().y - rect.top

      if (dragIdx < hoverIdx && clientY < midY) return
      if (dragIdx > hoverIdx && clientY > midY) return

      onMoveGroup(dragIdx, hoverIdx)
      item.groupIndex = hoverIdx
    }
  })

  drag(drop(ref))

  return (
    <div
      ref={ref}
      className={className}
      style={{ opacity: isDragging ? 0.4 : 1 }}
      {...rest}
    >
      {children}
    </div>
  )
}

DraggableGroupRow.propTypes = {
  groupIndex: PropTypes.number.isRequired,
  children: PropTypes.node,
  onMoveGroup: PropTypes.func.isRequired,
  className: PropTypes.string
}

function DraggableFieldRow ({ groupIndex, fieldIndex, fieldId, children, onMoveField, className, ...rest }) {
  const ref = useRef(null)
  const dragEnabled = fieldIndex >= 0

  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPES.FIELD,
    item: () => ({ groupIndex, fieldIndex, fieldId }),
    canDrag: () => dragEnabled,
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  })

  const [, drop] = useDrop({
    accept: DRAG_TYPES.FIELD,
    hover (item, monitor) {
      if (!dragEnabled) return
      if (!ref.current) return
      if (item.groupIndex !== groupIndex) return
      const dragIdx = item.fieldIndex
      const hoverIdx = fieldIndex
      if (dragIdx === hoverIdx) return

      const rect = ref.current.getBoundingClientRect()
      const midY = (rect.bottom - rect.top) / 2
      const clientY = monitor.getClientOffset().y - rect.top

      if (dragIdx < hoverIdx && clientY < midY) return
      if (dragIdx > hoverIdx && clientY > midY) return

      onMoveField(groupIndex, dragIdx, hoverIdx)
      item.fieldIndex = hoverIdx
    }
  })

  drag(drop(ref))

  return (
    <div
      ref={ref}
      className={className}
      style={{ opacity: isDragging ? 0.4 : 1, cursor: dragEnabled ? 'grab' : 'pointer' }}
      {...rest}
    >
      {children}
    </div>
  )
}

DraggableFieldRow.propTypes = {
  groupIndex: PropTypes.number.isRequired,
  fieldIndex: PropTypes.number.isRequired,
  fieldId: PropTypes.string.isRequired,
  children: PropTypes.node,
  onMoveField: PropTypes.func.isRequired,
  className: PropTypes.string
}

export { DraggableGroupRow, DraggableFieldRow, DRAG_TYPES }
